import * as React from 'react';
import path from 'path';
import {
  doesInstallerFileExist,
  downloadInstaller,
  getInstallerUrl,
  openExecutableFile,
} from './utils/file.utils';

import css from './main.module.scss';
import { LoaderAutocut } from './components/LoaderAutocut/LoaderAutocut';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../i18n/use-translation';
import { LoadingDots } from './components/LoadingDots/LoadingDots';
import { ClosingCountdown } from './components/ClosingCountdown/ClosingCountdown';
import {
  captureExchangeEvent,
  captureExchangeFailureOnce,
  getSessionElapsedMs,
} from './utils/analytics/analytics.utils';
import { ExchangeEvent, FailureStep } from './utils/analytics/events';
import { findInstalledDesktopAppPath } from './utils/folder.util';

type Phase = 'downloading' | 'launched' | 'already-installed' | 'failed';

export const App = () => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  // Lazy initializer, not an effect: resolving this during the first render is
  // what keeps an already-installed user from seeing a frame of "Downloading".
  const [installedAppPath] = useState(findInstalledDesktopAppPath);
  const [phase, setPhase] = useState<Phase>(
    installedAppPath ? 'already-installed' : 'downloading'
  );
  const progressRef = useRef(0);

  useEffect(() => {
    if (installedAppPath) {
      // No path in the payload: on Windows it embeds the username.
      captureExchangeEvent(ExchangeEvent.AlreadyInstalled, {
        detected_via: 'app_bundle',
      });
      return;
    }

    let failureStep: FailureStep = FailureStep.Download;

    const handleInstallerDownload = async () => {
      const installerUrl = getInstallerUrl();

      captureExchangeEvent(ExchangeEvent.DownloadStarted, {
        // basename only: the full path sits under os.tmpdir(), which embeds the
        // username on Windows.
        installer_name: path.basename(installerUrl),
        installer_url: installerUrl,
        installer_existed_before: doesInstallerFileExist(),
      });

      const download = await downloadInstaller(percent => {
        progressRef.current = percent;
        setProgress(percent);
      });

      captureExchangeEvent(ExchangeEvent.DownloadCompleted, {
        duration_ms: download.durationMs,
        bytes_total: download.totalBytes,
        bytes_downloaded: download.downloadedBytes,
        bytes_match:
          download.totalBytes > 0 &&
          download.totalBytes === download.downloadedBytes,
        avg_kbps:
          download.durationMs > 0
            ? Math.round(download.downloadedBytes / download.durationMs)
            : 0,
        redirects: download.redirects,
        http_status: download.statusCode,
      });

      failureStep = FailureStep.Launch;
      const launch = await openExecutableFile(download.filePath);

      captureExchangeEvent(ExchangeEvent.InstallerLaunched, {
        launch_method: launch.method,
        launch_duration_ms: launch.durationMs,
        download_duration_ms: download.durationMs,
        total_duration_ms: getSessionElapsedMs(),
      });

      setPhase('launched');
      setProgress(100);
    };

    void handleInstallerDownload().catch((error: unknown) => {
      captureExchangeFailureOnce(failureStep, error, {
        progress_percent: progressRef.current,
      });
      setPhase('failed');
    });
  }, [installedAppPath]);

  const hasFailed = phase === 'failed';
  const isAlreadyInstalled = phase === 'already-installed';
  const isDownloading = phase === 'downloading';

  return (
    <div className={css.container}>
      <div className={css.header}>
        <h1>{t('title')}</h1>
        <h3>
          {isDownloading ? (
            <>
              {t('downloading')}
              <LoadingDots />
            </>
          ) : hasFailed ? (
            <>{t('errorTitle')}</>
          ) : isAlreadyInstalled ? (
            <>{t('alreadyInstalledTitle')}</>
          ) : (
            <>{t('downloadedTitle')}</>
          )}
        </h3>
        {hasFailed && <p>{t('errorHelp')}</p>}
        {isAlreadyInstalled && <p>{t('alreadyInstalledHelp')}</p>}
      </div>
      <div className={css.content}>
        <LoaderAutocut
          height={150}
          progress={isAlreadyInstalled ? 100 : progress}
          isLoading={!hasFailed && !isAlreadyInstalled}
        />
      </div>
      <p>
        {isDownloading ? (
          `${progress}%`
        ) : (
          <ClosingCountdown
            reason={
              hasFailed
                ? 'failure'
                : isAlreadyInstalled
                ? 'already-installed'
                : 'success'
            }
          />
        )}
      </p>
    </div>
  );
};
